import { Link } from 'react-router-dom';

const VALUES = [
  { icon: '🤝🏿', title: 'Community First', desc: 'Every dollar that flows through Starfuel stays in the Black community. We built infrastructure that helps you build wealth — not a marketplace that extracts it.' },
  { icon: '💰', title: 'Fair Pay Always', desc: 'Drivers are guaranteed $5 minimum per delivery. Period. We believe the people doing the work deserve to be paid fairly, every single time.' },
  { icon: '📊', title: 'Transparent Pricing', desc: 'Flat rates. No percentage cuts. No surge pricing. No hidden fees. You always know exactly what you are paying before you pay it.' },
  { icon: '🚗', title: 'Reliability Over Everything', desc: 'A missed delivery hurts your reputation. We built real-time tracking, instant driver notifications, and automatic assignment so your orders arrive on time.' },
];

export default function About() {
  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="border-b border-gray-800 px-4 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="font-black text-2xl text-yellow-400" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>STARFUEL</Link>
        <div className="flex items-center gap-3">
          <Link to="/pricing" className="text-gray-400 hover:text-white text-sm transition">Pricing</Link>
          <Link to="/faq" className="text-gray-400 hover:text-white text-sm transition">FAQ</Link>
          <Link to="/contact" className="text-gray-400 hover:text-white text-sm transition">Contact</Link>
          <Link to="/login" className="text-gray-400 hover:text-white text-sm transition">Sign In</Link>
          <Link to="/register" className="px-4 py-2 bg-yellow-400 text-black text-sm font-bold rounded-xl hover:bg-yellow-300 transition">Get Started</Link>
        </div>
      </nav>
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-16">
        <div className="mb-20">
          <p className="text-yellow-400 font-mono text-xs tracking-widest uppercase mb-6">Our Story</p>
          <h1 className="text-5xl md:text-8xl font-black leading-none mb-8" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>BUILT FOR<br /><span className="text-yellow-400">BLACK-OWNED</span><br />BUSINESSES.</h1>
          <div className="max-w-3xl space-y-5 text-lg text-gray-300 leading-relaxed">
            <p>Black-owned restaurants, caterers, and food businesses have always been the backbone of our communities. But when it came to delivery, they were forced to choose between doing it themselves or handing 15-30% of every order to corporations that do not invest back in our neighborhoods.</p>
            <p><strong className="text-white">Starfuel CarFleet is different.</strong> We built a delivery fleet platform specifically for Black-owned businesses — with flat rates, transparent pricing, and a guaranteed minimum for every driver. When you use Starfuel, your money stays in your community.</p>
            <p>We are not a marketplace. We are infrastructure. We help you build your business, serve your customers, and keep what you earn.</p>
          </div>
        </div>
        <div className="border-l-4 border-yellow-400 pl-8 py-4 mb-20">
          <p className="text-2xl md:text-3xl font-light text-white leading-relaxed">"Keep Black dollars moving. Every delivery through Starfuel is wealth staying in our community — not leaving it."</p>
          <p className="text-yellow-400 font-mono text-xs tracking-widest uppercase mt-4">— Starfuel Enterprises, Inc. / Tulsa, Oklahoma</p>
        </div>
        <div className="mb-20">
          <p className="text-yellow-400 font-mono text-xs tracking-widest uppercase mb-8">What We Stand For</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {VALUES.map(v => (
              <div key={v.title} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-yellow-400/40 transition">
                <div className="text-3xl mb-4">{v.icon}</div>
                <h3 className="font-bold text-white text-lg mb-2">{v.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-2xl p-8 mb-20">
          <p className="text-yellow-400 font-mono text-xs tracking-widest uppercase mb-6">Company</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { label: 'Legal Name', value: 'Starfuel Enterprises, Inc.' },
              { label: 'Product', value: 'Starfuel CarFleet & Chauffeur Service' },
              { label: 'Incorporated', value: 'State of Oklahoma' },
              { label: 'Headquarters', value: 'Tulsa, Oklahoma' },
              { label: 'Founded', value: '2026' },
              { label: 'Contact', value: 'legal@starfuelcarfleet.com' },
            ].map(item => (
              <div key={item.label}>
                <p className="text-xs text-gray-500 font-mono uppercase tracking-wider mb-1">{item.label}</p>
                <p className="text-white font-medium">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="text-center">
          <h2 className="text-4xl font-black text-white mb-4" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>Ready to join the movement?</h2>
          <p className="text-gray-400 mb-8">Sign up free — restaurants and drivers welcome.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/register" className="px-8 py-3.5 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition">Sign Up as Restaurant →</Link>
            <Link to="/register?role=driver" className="px-8 py-3.5 border border-gray-600 text-white rounded-xl hover:border-gray-400 transition">Sign Up as Driver</Link>
          </div>
        </div>
      </main>
      <footer className="border-t border-gray-800 px-4 md:px-8 py-8 mt-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-yellow-400 font-black text-xl" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>STARFUEL</p>
          <p className="text-gray-600 text-sm">© 2026 Starfuel Enterprises, Inc. — Tulsa, Oklahoma</p>
          <div className="flex gap-4 text-sm text-gray-500"><Link to="/terms" className="hover:text-white transition">Terms</Link><Link to="/privacy" className="hover:text-white transition">Privacy</Link><Link to="/contact" className="hover:text-white transition">Contact</Link></div>
        </div>
      </footer>
    </div>
  );
}
